import { useState } from 'react'
import { isEmpty } from 'lodash'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Container,
  Grid,
  TextField,
  Typography
} from '@mui/material'
import type { GridColDef } from '@mui/x-data-grid'
import { DataGrid } from '@mui/x-data-grid'
import { useSelector } from 'react-redux'
import { ExpandMore } from '@mui/icons-material'
import Content from 'components/content/content'
import Loading from 'components/loading/loading'
import type { ErrorProps, RolesAssignmentMenuProps, UserProps } from 'modules/types'
import { useGetAccountPermissionDetailQuery } from 'store/assignment'
import { useGetUsersAccountDetailQuery } from 'store/control'
import { useGetMenuTreeQuery } from 'store/menu'
import Notification from 'components/notification'
import GLOBAL from 'modules/global'
import AccessStyle from './style'

const Access = () => {
  const state = useSelector((state: { user: UserProps }) => state.user)

  const {
    data: dataAccount,
    error: dataAccountError,
    isFetching: isAccountFetching,
    isLoading: isAccountLoading,
    isUninitialized: isACcountUninitialized,
    isSuccess: isAccountSuccess,
    isError: isAccountError
  } = useGetUsersAccountDetailQuery(state.user.id)

  const {
    data: dataPermission,
    isFetching: isPermissionFetching,
    isLoading: isPermissionLoading
  } = useGetAccountPermissionDetailQuery(state.user.id)

  const {
    data: dataMenu,
    isFetching: isMenuFetching,
    isLoading: isMenuLoading
  } = useGetMenuTreeQuery()

  const selectedColumns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 96 },
    { field: 'menu', headerName: 'Main Menu', width: 192 },
    { field: 'name', headerName: 'Sub Menu', width: 192 },
    { field: 'convertedFunctions', headerName: 'Function Name', flex: 1 }
  ]

  const selectedMainMenu =
    dataMenu &&
    dataMenu.mainMenu &&
    dataMenu.mainMenu
      .map((main) => {
        const subMenu = main.subMenu.map((subMenu) => {
          const functions = subMenu.listFunction.map((func) => ({
            id: func.functionId,
            name: func.name,
            type: func.type
          }))

          const list = {
            menu: main.name,
            name: subMenu.name,
            id: subMenu.subMenuId,
            functions: functions,
            convertedFunctions: functions.map((func) => func.name).join(', ')
          }

          return list
        })
        return subMenu
      })
      .filter((menu) => menu.length)
      .flat(1)

  const userName = (dataAccount && dataAccount.userName) || '-'
  const email = (dataAccount && dataAccount.email) || '-'
  const details = dataAccount && dataAccount.usersDetails && dataAccount.usersDetails[0]
  const fullName =
    (details &&
      details.firstName &&
      details.lastName &&
      details.firstName + ' ' + details.lastName) ||
    (details && details.firstName) ||
    '-'
  const mobilePhone = (details && details.mobilePhone) || '-'

  const gridDataLabel = ['Full Name', 'User Name', 'email', 'Mobile Phone']
  const gridDataValue = [fullName, userName, email, mobilePhone]

  const [selectedRow, setSelectedRow] = useState<(RolesAssignmentMenuProps | undefined)[]>(
    selectedMainMenu as (RolesAssignmentMenuProps | undefined)[]
  )

  const isLoading =
    (isAccountLoading || isAccountFetching) &&
    !dataAccount &&
    (isPermissionLoading || isPermissionFetching) &&
    !dataPermission &&
    (isMenuLoading || isMenuFetching) &&
    !dataMenu

  if (!selectedRow && isEmpty(selectedRow) && selectedMainMenu) {
    setSelectedRow(selectedMainMenu)
  }

  return (
    <>
      <Content title='My Access' useTable={false} isLoading={isLoading}>
        {isLoading && <Loading />}
        <Container {...AccessStyle.Container}>
          {dataAccount && (
            <Accordion elevation={0} disableGutters expanded>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls='panel1a-content'
                id='panel1a-header'
              >
                <Typography>Basic Information</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Container {...AccessStyle.Basic}>
                  {gridDataValue.map((item, index) => (
                    <TextField
                      key={index}
                      variant='outlined'
                      label={gridDataLabel[index]}
                      value={item}
                      inputProps={{ readOnly: true }}
                      multiline
                      fullWidth
                    />
                  ))}
                </Container>
              </AccordionDetails>
            </Accordion>
          )}

          {selectedRow && !isEmpty(selectedRow) && (
            <Accordion elevation={0} disableGutters expanded>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls='panel1a-content'
                id='panel1a-header'
              >
                <Typography>Access</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid item xs={12}>
                  <DataGrid rows={selectedRow} columns={selectedColumns} hideFooter autoHeight />
                </Grid>
              </AccordionDetails>
            </Accordion>
          )}
        </Container>
      </Content>

      <Notification
        open={!isAccountLoading && !isACcountUninitialized && !isAccountSuccess}
        onClose={() => (isAccountError ? {} : location.reload())}
        isError={isAccountError}
        message={GLOBAL.returnExceptionMessage(isAccountError, dataAccountError as ErrorProps)}
      />
    </>
  )
}

export default Access
